import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@material-ui/icons/Info';
import { Box, Tooltip } from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import { useUser } from '../../../utils/hooks/auth';
import { useDateTimeService } from '../../../utils/hooks/dateTimeService';
import { usePriceService } from '../../../utils/hooks/priceService';
import { getFullName, localizeEnum } from '../../../utils';
import {
    ColoredTwoLineCard,
    HighlightedInfoItemWrapper,
    HighlightedInfoWrapper,
    LargeDateInfo,
    LargeInfo,
    Typography,
} from '../../../ui-kit';
import { DeductionReason } from '../../../generated/graphql';
import { deductionReasonTooltipMap } from './constants';
import { DeductionList } from './DeductionListsTable/types';

interface DeductionListDetailsSectionProps {
    deductionList: DeductionList;
}

const DeductionListDetailsSection = ({
    deductionList: { createdBy, deductionDateUtc, deductionListItemsCount, deductionListItemsTotalCost, deductionReason }
}: DeductionListDetailsSectionProps) => {
    const { t } = useTranslation();
    const { user: { cultureCode, defaultCurrency } } = useUser();
    const { dateTimeService } = useDateTimeService();
    const { priceService } = usePriceService();

    const deductionReasonTooltip = t(...deductionReasonTooltipMap[deductionReason]);
    const fullName = getFullName(createdBy);
    const deductedAmount = priceService.toLocaleString(
        deductionListItemsTotalCost,
        defaultCurrency.currencyCode
    );
    const localizedDeductionReason = localizeEnum(t)(deductionReason, { DeductionReason });
    const localizedDeductionDate = dateTimeService.localizeDate(deductionDateUtc, cultureCode);
    const localizedDeductionTime = dateTimeService.localizeTime(deductionDateUtc, cultureCode);

    return (
        <>
            <HighlightedInfoWrapper>
                <HighlightedInfoItemWrapper>
                    <LargeInfo
                        Icon={() => (
                            <Tooltip title={deductionReasonTooltip}>
                                <InfoIcon color='primary' />
                            </Tooltip>
                    )}
                        text={localizedDeductionReason}
                        label={t('Deduction reason')}
                    />
                </HighlightedInfoItemWrapper>
                <HighlightedInfoItemWrapper>
                    <Box display='grid'>
                        <LargeDateInfo
                            label={t('Deduction date')}
                            text={localizedDeductionDate}
                        />
                        <Typography variant='overline' align='right'>
                            {localizedDeductionTime}
                        </Typography>
                    </Box>
                </HighlightedInfoItemWrapper>
                <HighlightedInfoItemWrapper overflow>
                    <LargeInfo
                        Icon={AccountCircleIcon}
                        text={fullName}
                        label={t('Deducted by')}
                    />
                </HighlightedInfoItemWrapper>
            </HighlightedInfoWrapper>
            <HighlightedInfoWrapper>
                <HighlightedInfoItemWrapper>
                    <ColoredTwoLineCard
                        label={t('Total cost')}
                        value={deductedAmount}
                        variant="error"
                    />
                </HighlightedInfoItemWrapper>
                <HighlightedInfoItemWrapper>
                    <ColoredTwoLineCard
                        label={t('Items count')}
                        value={deductionListItemsCount}
                        variant="error"
                    />
                </HighlightedInfoItemWrapper>
            </HighlightedInfoWrapper>
        </>
    );
};

export default DeductionListDetailsSection;